import React, { useEffect, useState, useCallback } from "react";
import {
  DashboardLayout,
  DeletePopupModal,
  PopupModal,
} from "../Components";
import axiosConfig from "../Service/axiosConfig";
import { MdDeleteOutline, MdOutlineEdit, MdDragIndicator, MdSearch } from "react-icons/md";
import { FaHandHoldingHeart } from "react-icons/fa";
import DataTable from "react-data-table-component";
import AlertDeletePopupModal from "../Components/PopupModal/AlertDeletePopupModal";
import { Modal, Button } from "react-bootstrap";
import { NavbarComponent } from "../Components/navbar/NavComponent";
import useDebouncedValue from "../Utils/Debounce";
import { useURLParams } from "../Components/UrlUtils/useURLParams";
import Select from "react-select";
import "./master_categories.css";
import "./CategorySearchSuggestions.css";

const CategorySearchSuggestions = () => {
  const { setParam, getParam } = useURLParams();

  // ─── Table / Pagination ────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [Data, SetData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [RowPerPage, setRowPerPage] = useState(20);

  // ─── Search & Filter ───────────────────────────────────────────────────────
  const [search, SetSearch] = useState(getParam("search") || "");
  const [filterCategory, setFilterCategory] = useState(getParam("category") || "");
  const [filterActive, setFilterActive] = useState(getParam("is_active") || "");
  const debouncedSearch = useDebouncedValue(search, 500);

  // ─── Modal ─────────────────────────────────────────────────────────────────
  const [show, setShow] = useState(false);
  const [modalTitle, setModalTitle] = useState("Add Suggestion");
  const [formData, setFormData] = useState({ category: null, product: null, sort: "", is_active: true });
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  // ─── Popup feedback ────────────────────────────────────────────────────────
  const [popupType, setpopupType] = useState("");
  const [message, setMessage] = useState("");
  const [deletePopupType, setDeletepopupType] = useState("");
  const [AlertDeletePopupType, SetAlertDeletePopupType] = useState("");
  const [RowId, SetRowId] = useState();

  // ─── Dropdown Options & Mappings ───────────────────────────────────────────
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [categoryMap, setCategoryMap] = useState({});
  const [productMap, setProductMap] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(false);

  // ── Fetch categories for dropdown & map ─────────────────────────────────────
  const fetchCategories = async () => {
    try {
      const res = await axiosConfig.get("catlog/categories/?page_size=500");
      const results = res.data.results || res.data;
      const opts = results.map((c) => ({
        value: c.id,
        label: c.name || c.category_name,
      }));
      setCategoryOptions(opts);

      const cMap = {};
      results.forEach((c) => {
        cMap[c.id] = c.name || c.category_name;
      });
      setCategoryMap(cMap);
    } catch (err) {
      console.error("Failed to load categories", err);
    }
  };

  // ── Fetch all products to resolve product names in table ────────────────────
  const fetchAllProductsMap = async () => {
    try {
      const res = await axiosConfig.get("catlog/products/?page_size=1000");
      const results = res.data.results || res.data;
      const pMap = {};
      results.forEach((p) => {
        pMap[p.id] = p.product_name || p.name || p.title;
      });
      setProductMap((prev) => ({ ...prev, ...pMap }));
    } catch (err) {
      console.error("Failed to load products map", err);
    }
  };

  // ── Fetch products filtered by selected category ───────────────────────────
  const fetchProducts = useCallback(async (categoryId) => {
    if (!categoryId) {
      setProductOptions([]);
      return;
    }
    setLoadingProducts(true);
    try {
      const res = await axiosConfig.get(
        `catlog/products/?category=${categoryId}&page_size=500`
      );
      const results = res.data.results || res.data;
      const opts = results.map((p) => ({
        value: p.id,
        label: p.product_name || p.name || p.title,
      }));
      setProductOptions(opts);

      const pMap = {};
      results.forEach((p) => {
        pMap[p.id] = p.product_name || p.name || p.title;
      });
      setProductMap((prev) => ({ ...prev, ...pMap }));
    } catch (err) {
      console.error("Failed to load products", err);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  // ─── List Data ─────────────────────────────────────────────────────────────
  const GetData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("page_size", RowPerPage);
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (filterCategory) params.append("category", filterCategory);
      if (filterActive !== "") params.append("is_active", filterActive);

      const res = await axiosConfig.get(
        `catlog/category-search-suggestions/?${params.toString()}`
      );
      SetData(res.data.results || res.data);
      setTotalRows(res.data.count || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchAllProductsMap();
  }, []);

  useEffect(() => {
    GetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, RowPerPage, debouncedSearch, filterCategory, filterActive]);

  // ─── Open/Close modal ──────────────────────────────────────────────────────
  const handleClose = () => {
    setShow(false);
    setFormErrors({});
    setSubmitError("");
  };

  const handleShowAdd = () => {
    setFormData({ category: null, product: [], sort: "", is_active: true });
    setProductOptions([]);
    setFormErrors({});
    setSubmitError("");
    setModalTitle("Add Suggestion(s)");
    setShow(true);
  };

  const handleEdit = async (id) => {
    try {
      const res = await axiosConfig.get(`catlog/category-search-suggestions/${id}/`);
      const d = res.data;
      const catOpt = categoryOptions.find((c) => c.value === (d.category?.id || d.category)) || null;
      const catId = catOpt?.value || d.category;
      if (catId) await fetchProducts(catId);
      
      const prodName = d.product?.product_name || d.product?.name || productMap[d.product?.id || d.product] || "Product";

      setFormData({
        id: d.id,
        category: catOpt || { value: d.category, label: categoryMap[d.category] || d.category },
        product: d.product ? { value: d.product?.id || d.product, label: prodName } : null,
        sort: d.sort ?? "",
        is_active: d.is_active ?? true,
      });
      setFormErrors({});
      setSubmitError("");
      setModalTitle("Edit Suggestion");
      setShow(true);
    } catch (err) {
      console.error(err);
    }
  };

  // ─── Inline toggle for is_active ──────────────────────────────────────────
  const handleToggleActive = async (row) => {
    try {
      await axiosConfig.patch(`catlog/category-search-suggestions/${row.id}/`, {
        is_active: !row.is_active,
      });
      GetData();
    } catch (err) {
      console.error("Toggle failed", err);
    }
  };

  // ─── Form Submit (Add / Edit) ──────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!formData.category) errors.category = "Category is required.";
    
    if (!formData.product || (Array.isArray(formData.product) && formData.product.length === 0)) {
      errors.product = "At least one product is required.";
    }
    
    if (formData.sort === "") errors.sort = "Sort order is required.";
    
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    try {
      if (formData.id) {
        // Edit mode (Single product)
        const selectedProd = Array.isArray(formData.product) ? formData.product[0] : formData.product;
        const payload = {
          category: formData.category.value,
          product: selectedProd.value,
          sort: Number(formData.sort),
          is_active: formData.is_active,
        };
        await axiosConfig.patch(
          `catlog/category-search-suggestions/${formData.id}/`,
          payload
        );
        setMessage("Suggestion updated successfully.");
      } else {
        // Add mode (Multiple or Single products)
        const selectedProducts = Array.isArray(formData.product) ? formData.product : [formData.product];
        const baseSort = Number(formData.sort) || 1;

        const promises = selectedProducts.map((prodOpt, index) => {
          const payload = {
            category: formData.category.value,
            product: prodOpt.value,
            sort: baseSort + index,
            is_active: formData.is_active,
          };
          return axiosConfig.post("catlog/category-search-suggestions/", payload);
        });

        await Promise.all(promises);
        setMessage(`${selectedProducts.length} suggestion(s) added successfully.`);
      }

      setpopupType("success");
      handleClose();
      GetData();
    } catch (err) {
      console.error(err);
      const detail =
        err?.response?.data?.non_field_errors?.[0] ||
        err?.response?.data?.detail ||
        err?.response?.data?.product?.[0] ||
        "Something went wrong. Please check your inputs.";
      setSubmitError(detail);
      setpopupType("error");
    }
  };

  // ─── Delete handlers ───────────────────────────────────────────────────────
  const DeleteAlert = (id) => {
    SetRowId(id);
    SetAlertDeletePopupType("success");
  };
  const CancelAlerrt = () => SetAlertDeletePopupType("error");

  const HandleDelete = async (id) => {
    try {
      await axiosConfig.delete(`catlog/category-search-suggestions/${id}/`);
      SetAlertDeletePopupType("error");
      setDeletepopupType("success");
      GetData();
    } catch (err) {
      setDeletepopupType("error");
      SetAlertDeletePopupType("error");
      console.error(err);
    }
  };

  const handleRevive = async (id) => {
    try {
      await axiosConfig.post(`catlog/category-search-suggestions/${id}/revive/`);
      setpopupType("success");
      setMessage("Suggestion revived successfully.");
      GetData();
    } catch (err) {
      console.error("Revive failed", err);
      setpopupType("error");
    }
  };


  // ─── Table Columns ─────────────────────────────────────────────────────────
  const tableCustomStyles = {
    headCells: {
      style: {
        color: "rgb(33,37,41)",
        fontSize: "12px",
        fontWeight: 500,
        lineHeight: "28px",
      },
    },
  };

  const getCategoryName = (row) => {
    if (typeof row.category === "object" && row.category !== null) {
      return row.category.name || row.category.category_name || row.category.id;
    }
    return row.category_name || categoryMap[row.category] || `Category #${row.category}`;
  };

  const getProductName = (row) => {
    if (typeof row.product === "object" && row.product !== null) {
      return row.product.product_name || row.product.name || row.product.title || row.product.id;
    }
    return row.product_name || productMap[row.product] || `Product #${row.product}`;
  };

  const columns = [
    {
      name: "Sort",
      selector: (row) => row.sort,
      cell: (row) => (
        <span className="css-sort-badge">{row.sort}</span>
      ),
      width: "100px",
      sortable: true,
    },
    {
      name: "Category",
      selector: (row) => getCategoryName(row),
      cell: (row) => (
        <span className="css-cell-text">
          {getCategoryName(row)}
        </span>
      ),
      sortable: true,
      reorder: true,
    },
    {
      name: "Product",
      selector: (row) => getProductName(row),
      cell: (row) => (
        <span className="css-cell-text">
          {getProductName(row)}
        </span>
      ),
      sortable: true,
      reorder: true,
    },
    {
      name: "Active",
      cell: (row) => (
        <label className="css-toggle-switch">
          <input
            type="checkbox"
            checked={row.is_active}
            onChange={() => handleToggleActive(row)}
          />
          <span className="css-toggle-slider" />
        </label>
      ),
      width: "90px",
    },
    {
      name: "ACTIONS",
      cell: (row) => (
        <>
          {!row.is_suspended ? (
            <div className="row">
              <div className="col-4">
                <MdOutlineEdit
                  className="dashboard-tabe-data-actions"
                  onClick={() => handleEdit(row.id)}
                />
              </div>
              <div className="col-4">
                <MdDeleteOutline
                  className="dashboard-tabe-data-actions"
                  onClick={() => DeleteAlert(row.id)}
                />
              </div>
            </div>
          ) : (
            <FaHandHoldingHeart 
              className="dashboard-tabe-data-actions" 
              onClick={() => handleRevive(row.id)}
            />
          )}
          {AlertDeletePopupType === "success" && RowId === row.id && (
            <AlertDeletePopupModal
              AlertDeletePopupType={AlertDeletePopupType}
              HandleDelete={HandleDelete}
              CancelAlerrt={CancelAlerrt}
              row={RowId}
            />
          )}
        </>
      ),
      reorder: true,
    },
  ];

  return (
    <>
      <div className="child-sidenav">
        <NavbarComponent />
        <div className="childnav-content">
          {/* ── Header Bar ── */}
          <div className="css-suggestions-header d-flex align-items-center gap-2 flex-wrap">
            {/* Search */}
            <div className="input-cat-with-icon flex-grow-1">
              <span className="icon-product">
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="input-control"
                placeholder="Search by product or category name…"
                value={search}
                onChange={(e) => {
                  SetSearch(e.target.value);
                  setParam("search", e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* Category Filter */}
            <div className="css-filter-select">
              <Select
                placeholder="Filter by Category"
                isClearable
                options={categoryOptions}
                value={categoryOptions.find((c) => String(c.value) === String(filterCategory)) || null}
                onChange={(opt) => {
                  const val = opt ? String(opt.value) : "";
                  setFilterCategory(val);
                  setParam("category", val);
                  setCurrentPage(1);
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  control: (b) => ({ ...b, minWidth: 200, fontSize: 13 }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div>

            {/* Active Filter */}
            <div className="css-filter-select">
              <Select
                placeholder="Filter by Status"
                isClearable
                options={[
                  { value: "true", label: "Active" },
                  { value: "false", label: "Inactive" },
                ]}
                value={
                  filterActive === "true"
                    ? { value: "true", label: "Active" }
                    : filterActive === "false"
                    ? { value: "false", label: "Inactive" }
                    : null
                }
                onChange={(opt) => {
                  const val = opt ? opt.value : "";
                  setFilterActive(val);
                  setParam("is_active", val);
                  setCurrentPage(1);
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  control: (b) => ({ ...b, minWidth: 180, fontSize: 13 }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div>

            {/* Add Button */}
            <div className="cat-button">
              <div className="btn" onClick={handleShowAdd}>
                + Add Suggestion
              </div>
            </div>
          </div>

          {/* ── DataTable ── */}
          <div className="table-responsive marginTop">
            <DataTable
              columns={columns}
              data={Data}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={RowPerPage}
              paginationDefaultPage={currentPage}
              onChangePage={(page) => setCurrentPage(page)}
              paginationPerPageOptions={[10, 20, 25, 30, 40, 50]}
              paginationRowsPerPageOptions={[10, 20, 25, 30, 40, 50]}
              onChangeRowsPerPage={(n) => { setRowPerPage(n); setCurrentPage(1); }}
              fixedHeader
              customStyles={tableCustomStyles}
              progressPending={loading}
              progressComponent={
                <div className="loader-container">
                  <img
                    src={require("../Components/FormParser/images/loader2.gif")}
                    alt="Loading…"
                  />
                </div>
              }
              noDataComponent={
                <div className="css-empty-state">
                  <p>No search suggestions configured yet.</p>
                  <button className="btn btn-sm css-add-btn-empty" onClick={handleShowAdd}>
                    + Add First Suggestion
                  </button>
                </div>
              }
            />
          </div>
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      <Modal size="md" show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16, fontWeight: 600 }}>
            {modalTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit}>
            {/* Category */}
            <div className="mb-3">
              <label className="css-form-label">Category <span className="text-danger">*</span></label>
              <Select
                placeholder="Select Category…"
                options={categoryOptions}
                value={formData.category}
                onChange={(opt) => {
                  setFormData((prev) => ({ ...prev, category: opt, product: formData.id ? null : [] }));
                  if (opt) fetchProducts(opt.value);
                  else setProductOptions([]);
                  setFormErrors((p) => ({ ...p, category: "" }));
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
              {formErrors.category && <div className="css-field-error">{formErrors.category}</div>}
            </div>

            {/* Product */}
            <div className="mb-3">
              <label className="css-form-label">Product(s) <span className="text-danger">*</span></label>
              <Select
                isMulti={!formData.id}
                placeholder={
                  formData.category
                    ? formData.id
                      ? "Select Product…"
                      : "Select Product(s)…"
                    : "Select a category first"
                }
                isDisabled={!formData.category}
                isLoading={loadingProducts}
                options={productOptions}
                value={formData.product}
                onChange={(opt) => {
                  setFormData((prev) => ({ ...prev, product: opt }));
                  setFormErrors((p) => ({ ...p, product: "" }));
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
              {formErrors.product && <div className="css-field-error">{formErrors.product}</div>}
              <div className="css-hint">
                {formData.id
                  ? "Only products belonging to the selected category are shown."
                  : "You can select multiple products to add them all under this category at once."}
              </div>
            </div>

            {/* Sort */}
            <div className="mb-3">
              <label className="css-form-label">Sort Order <span className="text-danger">*</span></label>
              <input
                type="number"
                className={`form-control ${formErrors.sort ? "is-invalid" : ""}`}
                placeholder="e.g. 1"
                value={formData.sort}
                min={0}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, sort: e.target.value }));
                  setFormErrors((p) => ({ ...p, sort: "" }));
                }}
              />
              {formErrors.sort && <div className="invalid-feedback">{formErrors.sort}</div>}
              {!formData.id && (
                <div className="css-hint">Starting sort order (auto-increments for multiple selected products).</div>
              )}
            </div>

            {/* Active */}
            <div className="mb-4 d-flex align-items-center gap-3">
              <label className="css-form-label mb-0">Active</label>
              <label className="css-toggle-switch">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
                />
                <span className="css-toggle-slider" />
              </label>
              <span style={{ fontSize: 13, color: formData.is_active ? "#1b7d78" : "#999" }}>
                {formData.is_active ? "Active" : "Inactive"}
              </span>
            </div>

            {submitError && (
              <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>
                {submitError}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" size="sm" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                style={{ backgroundColor: "#1b7d78", borderColor: "#1b7d78" }}
              >
                {formData.id ? "Update" : "Add Suggestion(s)"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* ── Feedback popups ── */}
      {popupType !== "" && (
        <PopupModal
          popupType={popupType}
          setpopupType={setpopupType}
          Navigation="/dashboard/masters/category-search-suggestions"
          Message={message}
          error={submitError}
        />
      )}
      {deletePopupType !== "" && (
        <DeletePopupModal
          setDeletepopupType={setDeletepopupType}
          deletePopupType={deletePopupType}
        />
      )}
    </>
  );
};

export { CategorySearchSuggestions };
